import React, { useEffect, useState } from 'react';
import { Card, OverlayTrigger, Row, Table, Tooltip } from 'react-bootstrap';
import { IUser } from '../../../../model/user/IUser';
import { UserService } from '../../UserService';
import { Pagination } from '../../../../shared/components/Pagination';
import { useNavigate } from 'react-router';
import { ErrorToast } from '../../../../utils/toasters';
import { getCorrectEnums, getCorrectSocialUrl } from '../../../../utils/Functions';
import Flag from '../../../../shared/components/Flag';
import { ImpersonateEye } from '../../../../shared/components/ImpersonateEye';
import { UserFilter } from '../UserFilter';
import { ControllingCard } from '../../../controlling/components/ControllingCard';
import { FaPercentage } from 'react-icons/fa';
import { priceSplitter } from '../../../../utils/helpers/NumberFormater';
import EmptyState from '../../../../shared/components/EmptyState';
import TableSkeleton, { ITableColumn } from '../../../../shared/components/TableSkeleton';

interface IFeeInfo {
    activePreferredPayment: number;
    volumePreferredPayment: number;
    feePreferredPayment: number;
    activeIVClub: number;
    volumeIVClub: number;
    feeIVClub: number;
}

const initialInfo: IFeeInfo = {
    activePreferredPayment: 0,
    volumePreferredPayment: 0,
    feePreferredPayment: 0,
    activeIVClub: 0,
    volumeIVClub: 0,
    feeIVClub: 0,
};

const FEE_COLUMNS: ITableColumn[] = [
    { label: 'ID' },
    { label: 'Agency ID' },
    { label: 'Country' },
    { label: 'Influencer' },
    { label: 'Gender' },
    { label: 'Handles' },
    { label: 'Email' },
    { label: 'Campaigns' },
    { label: 'Type' },
    { label: 'Fee' },
];

export const InfluencerWithFeeList = () => {
    const navigate = useNavigate();
    const [state, setState] = useState({
        page: 1,
        perPage: 15,
        displayName: '',
        email: '',
        firstName: '',
        lastName: '',
        type: '',
        count: 0,
    });
    const [user, setUser] = useState<IUser[]>([]);
    const [change, setChange] = useState(false);
    const [totalPages, setTotalPages] = useState(0);
    const [info, setInfo] = useState<IFeeInfo>(initialInfo);
    const [dataLoading, setDataLoading] = useState(true);

    useEffect(() => {
        setDataLoading(true);
        UserService.getInfluencersWithFee(state)
            .then(data => {
                setUser(data?.data);
                setInfo(data?.info ?? initialInfo);
                setTotalPages(Math.ceil(data.count / state.perPage));
            })
            .catch(err => ErrorToast(err))
            .finally(() => setDataLoading(false));
    }, [change]);

    const handlePages = (updatePage: number) => {
        setState({ ...state, page: updatePage });
        setChange(prev => !prev);
    };

    function handleClick() {
        setState(prev => {
            return { ...prev, page: 1 };
        });
        setChange(prev => !prev);
    }

    function handleChange(
        evt: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>,
        isSelectBoolean?: boolean
    ) {
        const { name, value } = evt.target;

        let newValue: any = value;

        if (typeof newValue === 'string') {
            newValue = value.replace(/^@/, '');
        }

        if (isSelectBoolean) {
            if (value === 'null') {
                newValue = undefined;
            } else {
                newValue = value === 'enable';
            }
        }

        setState({ ...state, [name]: newValue });
    }

    return (
        <div>
            <div className="mb-5">
                <Row className="gap-y-4">
                    <ControllingCard
                        cardName="Active Preferred Payment"
                        icon={<FaPercentage />}
                        baseColor="primary"
                        total={info.activePreferredPayment}
                        styleCard="h-100"
                        withoutFormating={true}
                    />
                    <ControllingCard
                        cardName="Volume Preferred Payment"
                        icon={<FaPercentage />}
                        baseColor="primary"
                        total={info.volumePreferredPayment}
                        styleCard="h-100"
                        withoutFormating={true}
                    />
                    <ControllingCard
                        cardName="Fee Preferred Payment"
                        icon={<FaPercentage />}
                        baseColor="primary"
                        total={info.feePreferredPayment}
                        styleCard="h-100"
                    />
                    <ControllingCard
                        cardName="Active i.v. Club"
                        icon={<FaPercentage />}
                        baseColor="warning"
                        total={info.activeIVClub}
                        styleCard="h-100"
                        withoutFormating={true}
                    />
                    <ControllingCard
                        cardName="Volume i.v. Club"
                        icon={<FaPercentage />}
                        baseColor="warning"
                        total={info.volumeIVClub}
                        styleCard="h-100"
                        withoutFormating={true}
                    />
                    <ControllingCard
                        cardName="Fee i.v. Club"
                        icon={<FaPercentage />}
                        baseColor="warning"
                        total={info.feeIVClub}
                        styleCard="h-100"
                    />
                </Row>
            </div>
            <Card
                className="mb-5 p-4"
                onKeyDown={(e: any) => {
                    if (e.key === 'Enter') handleClick();
                }}
            >
                <UserFilter
                    handleChanges={handleChange}
                    handleClicks={handleClick}
                    setState={setState}
                    page={'influencer_fee'}
                />
            </Card>

            <Card className="mb-5 p-4">
                {dataLoading ? (
                    <>
                        <TableSkeleton columns={FEE_COLUMNS} rows={state.perPage} />
                        <Pagination page={state.page} totalPages={totalPages} handlePagination={handlePages} disabled />
                    </>
                ) : user.length === 0 ? (
                    <EmptyState classHolder="shadow-none" />
                ) : (
                    <>
                        <Table responsive className="table scroll-10 ">
                            <thead>
                                <tr className="even">
                                    {FEE_COLUMNS.map(column => (
                                        <th key={column.label} className={column.className}>
                                            {column.label}
                                        </th>
                                    ))}
                                </tr>
                            </thead>
                            <tbody>
                                {user?.map((e: IUser) => (
                                    <tr key={e.id}>
                                        <td>{e.id}</td>
                                        <td>{e?.agencyCompany?.id ?? 'n/a'}</td>
                                        <td>
                                            <Flag code={e?.contact?.country} />
                                        </td>
                                        <td>
                                            {e.isEnabled === false ? 'DISABLED' : e.displayName}
                                            <br />
                                            {e.isEnabled === false ? 'USER' : e.firstName + '-' + e.lastName}
                                        </td>
                                        <td>{e.gender ? getCorrectEnums(e.gender) : '-'}</td>
                                        <td>
                                            <div className="d-flex flex-wrap gap-2">
                                                {e?.handles?.slice(0, 3).map((item: any) => (
                                                    <OverlayTrigger
                                                        key={item.id}
                                                        placement="top"
                                                        overlay={<Tooltip>@{item?.title}</Tooltip>}
                                                    >
                                                        <a
                                                            target="_blank"
                                                            href={
                                                                getCorrectSocialUrl(
                                                                    item?.mainPlatform?.platformEnum as string,
                                                                    item?.title,
                                                                    item?.externalLink
                                                                ) ?? undefined
                                                            }
                                                            rel="noreferrer"
                                                            className="d-inline-flex align-items-center justify-content-center border rounded-circle"
                                                        >
                                                            <img
                                                                src={item?.mainPlatform?.path}
                                                                alt={item?.mainPlatform?.platformEnum}
                                                                width={35}
                                                                height={35}
                                                            />
                                                        </a>
                                                    </OverlayTrigger>
                                                ))}
                                                {e?.handles && e.handles.length > 3 && (
                                                    <OverlayTrigger
                                                        placement="top"
                                                        overlay={
                                                            <Tooltip>
                                                                {e.handles.slice(3).map((item: any) => (
                                                                    <div className="text-start" key={item.id}>
                                                                        <img
                                                                            src={item?.mainPlatform?.path}
                                                                            alt={item?.mainPlatform?.platformEnum}
                                                                            width={28}
                                                                            height={28}
                                                                        />
                                                                        @{item?.title}
                                                                    </div>
                                                                ))}
                                                            </Tooltip>
                                                        }
                                                    >
                                                        <span
                                                            className="d-inline-flex align-items-center justify-content-center border rounded-circle cursor-pointer"
                                                            style={{
                                                                width: 35,
                                                                height: 35,
                                                                fontSize: '0.8rem',
                                                                fontWeight: 600,
                                                                color: '#495057',
                                                                backgroundColor: '#f8f9fa',
                                                                cursor: 'default',
                                                            }}
                                                        >
                                                            +{e.handles.length - 3}
                                                        </span>
                                                    </OverlayTrigger>
                                                )}
                                            </div>
                                        </td>
                                        <td>
                                            <ImpersonateEye id={e.id ?? 0} />
                                            {e.email}
                                        </td>
                                        <td>{e?.feeCampaigns}</td>
                                        <td>{e.isIvConnectedClub ? 'iv Connected club' : 'Preferred payment'}</td>
                                        <td>{priceSplitter(e.offersPrice ?? 0)} €</td>
                                    </tr>
                                ))}
                            </tbody>
                            {user.length < 15 && !!user.length && <div className="h-180px"></div>}
                        </Table>

                        <Pagination page={state.page} totalPages={totalPages} handlePagination={handlePages} />
                    </>
                )}
            </Card>
        </div>
    );
};
